import type { CardId, WallBoard, WallCard } from '@walls/store';
import { getCardPermissions } from '../cards/hooks/useCardPermissions';
import type { MovableSelectedCard } from '../cards/utils/selectedCards';
import type { BoardRect } from '../selection/utils/selectionGeometry';
import { getBoardCardElements, getPositionedCardDimensions } from './cardRects';
import { hitTestBoardCards } from './hitTesting';

export interface ClusterDropResult {
  card: WallCard;
  cluster: WallCard;
  createCluster: boolean;
  placeholderBeforeChildId: CardId | null;
  size: {
    height: number;
    width: number;
  };
  zPos: number;
}

interface BoardPoint {
  x: number;
  y: number;
}

export function getClusterDropTarget({
  board,
  cards,
  currentParticipantId,
  deltaX,
  deltaY,
  draggedCards,
  pointerPosition,
  measuredRects,
}: {
  board: WallBoard;
  cards: WallCard[];
  currentParticipantId: string | null;
  deltaX: number;
  deltaY: number;
  draggedCards: MovableSelectedCard[];
  measuredRects?: Record<string, BoardRect>;
  pointerPosition?: BoardPoint | null;
}): ClusterDropResult | null {
  if (draggedCards.length !== 1) {
    return null;
  }

  const [draggedCard] = draggedCards;
  const droppedCard = {
    ...draggedCard.card,
    xPos: Math.round(draggedCard.startX + deltaX),
    yPos: Math.round(draggedCard.startY + deltaY),
  };
  const droppedCardDimensions = getPositionedCardDimensions(droppedCard);
  return getClusterDropTargetFromRect({
    board,
    card: droppedCard,
    cards,
    currentParticipantId,
    draggedRect: {
      height: droppedCardDimensions.height,
      width: droppedCardDimensions.width,
      x: droppedCard.xPos ?? 0,
      y: droppedCard.yPos ?? 0,
    },
    measuredRects,
    pointerPosition,
  });
}

export function getClusterDropTargetFromRect({
  board,
  card,
  cards,
  currentParticipantId,
  draggedRect,
  pointerPosition,
  measuredRects,
}: {
  board: WallBoard;
  card: WallCard;
  cards: WallCard[];
  currentParticipantId: string | null;
  draggedRect: {
    height: number;
    width: number;
    x: number;
    y: number;
  };
  measuredRects?: Record<string, BoardRect>;
  pointerPosition?: BoardPoint | null;
}): ClusterDropResult | null {
  const droppedCard = {
    ...card,
    xPos: Math.round(draggedRect.x),
    yPos: Math.round(draggedRect.y),
  };
  const droppedCardDimensions = {
    height: draggedRect.height,
    width: draggedRect.width,
  };
  const probe = pointerPosition ?? getRectCenter(draggedRect);
  const projectedCards = [...removeCardFromTree(cards, droppedCard.id), droppedCard];
  const hit = hitTestBoardCards(projectedCards, probe.x, probe.y, {
    excludedElementId: droppedCard.id,
    measuredRects,
  });

  if (!hit?.cluster || hit.cluster.id === droppedCard.id || hit.cluster.collapsed) {
    return null;
  }

  if (!getCardPermissions(board, hit.cluster, currentParticipantId).canSortClusterChildren) {
    return null;
  }

  if (isDescendantCluster(droppedCard, hit.cluster.id)) {
    return null;
  }

  const insertion = getClusterInsertion(projectedCards, hit.cluster, probe);

  return {
    card: droppedCard,
    cluster: hit.cluster,
    createCluster: false,
    placeholderBeforeChildId: insertion.placeholderBeforeChildId,
    size: droppedCardDimensions,
    zPos: insertion.zPos,
  };
}

function removeCardFromTree(cards: WallCard[], cardId: string): WallCard[] {
  return cards.flatMap((card) => {
    if (card.id === cardId) {
      return [];
    }

    return [{ ...card, cards: removeCardFromTree(card.cards, cardId) }];
  });
}

function getClusterInsertion(
  cards: WallCard[],
  cluster: WallCard,
  probe: BoardPoint
) {
  const orderedChildren = [...cluster.cards].sort((a, b) => (a.zPos ?? 0) - (b.zPos ?? 0));

  if (orderedChildren.length === 0) {
    return { placeholderBeforeChildId: null, zPos: 1 };
  }

  const elementRects = getBoardCardElements(cards);
  let lastPreviousIndex = -1;

  for (let childIndex = 0; childIndex < orderedChildren.length; childIndex += 1) {
    const childCard = orderedChildren[childIndex];
    const childElement = elementRects.find((element) => element.id === childCard.id);

    if (!childElement) {
      continue;
    }

    const { rect } = childElement;
    const isProbeInsideChild =
      probe.x > rect.x &&
      probe.x < rect.x + rect.width &&
      probe.y > rect.y &&
      probe.y < rect.y + rect.height;

    if (isProbeInsideChild) {
      const insertBeforeChild =
        cluster.width != null
          ? probe.x < rect.x + rect.width / 2
          : probe.y < rect.y + rect.height / 2;
      const insertionIndex = insertBeforeChild ? childIndex : childIndex + 1;

      return {
        placeholderBeforeChildId: orderedChildren[insertionIndex]?.id ?? null,
        zPos: insertBeforeChild
          ? (childCard.zPos ?? childIndex + 1)
          : (childCard.zPos ?? childIndex + 1) + 1,
      };
    }

    if (
      !cluster.width &&
      probe.x > rect.x &&
      probe.x < rect.x + rect.width &&
      probe.y > rect.y + rect.height
    ) {
      lastPreviousIndex = childIndex;
    }
  }

  if (lastPreviousIndex >= 0) {
    const previousChild = orderedChildren[lastPreviousIndex];
    const insertionIndex = lastPreviousIndex + 1;

    return {
      placeholderBeforeChildId: orderedChildren[insertionIndex]?.id ?? null,
      zPos: (previousChild.zPos ?? lastPreviousIndex + 1) + 1,
    };
  }

  const clusterElement = elementRects.find((element) => element.id === cluster.id);
  const beforeFirst =
    clusterElement != null && probe.y < clusterElement.rect.y + clusterElement.rect.height / 2;

  return beforeFirst
    ? { placeholderBeforeChildId: orderedChildren[0].id, zPos: orderedChildren[0].zPos ?? 1 }
    : {
        placeholderBeforeChildId: null,
        zPos: (orderedChildren[orderedChildren.length - 1].zPos ?? orderedChildren.length) + 1,
      };
}

function getRectCenter(rect: { height: number; width: number; x: number; y: number }): BoardPoint {
  return {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2,
  };
}

function isDescendantCluster(card: WallCard, clusterId: string): boolean {
  return card.cards.some(
    (childCard) => childCard.id === clusterId || isDescendantCluster(childCard, clusterId)
  );
}
